import React, { useEffect, useState } from 'react';
import {
  StatsigProvider as BaseStatsigProvider,
  useStatsigClient,
} from '@statsig/react-bindings';
import { StatsigTriggeredSessionReplayPlugin } from '@statsig/session-replay';
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics';

import { sanitizeMetadata } from './sanitizer';
import { setStatsigClientInstance } from './trackNoteTelemetryToStatsig';

// Type for LocalOverrideAdapter (imported dynamically)
type LocalOverrideAdapter = any;

interface StatsigProviderProps {
  children: React.ReactNode;
}

/**
 * Inner component that initializes the Statsig client for non-React contexts
 * Must be rendered inside BaseStatsigProvider to access useStatsigClient
 */
const StatsigClientInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { client } = useStatsigClient();

  useEffect(() => {
    // Initialize the client instance for use in non-React contexts (like NoteTelemetry)
    setStatsigClientInstance(client);

    if (import.meta.env.DEV || import.meta.env.VITE_BUILD_ENV === 'dev') {
      console.debug('[Statsig] Client instance initialized for NoteTelemetry tracking');
    }
  }, [client]);

  return <>{children}</>;
};

export const StatsigProviderWrapper: React.FC<StatsigProviderProps> = ({ children }) => {
  const sdkKey = import.meta.env.VITE_STATSIG_SDK_KEY as string;
  const [overrideAdapter, setOverrideAdapter] = useState<LocalOverrideAdapter | undefined>(
    undefined,
  );

  // Don't initialize if no SDK key
  if (!sdkKey) {
    console.warn('Statsig SDK key not found. Analytics will be disabled.');
    return <>{children}</>;
  }

  // Initialize autocapture plugin with PHI protection via sanitization
  const autoCapturePlugin = new StatsigAutoCapturePlugin({
    eventFilterFunc: event => {
      // Use existing sanitizer instead of blocking events
      if (event.metadata) {
        event.metadata = sanitizeMetadata(event.metadata);
      }
      return true;
    },
  });

  // Load override adapter dynamically in dev mode only
  useEffect(() => {
    if (import.meta.env.DEV && sdkKey) {
      import('@statsig/js-local-overrides')
        .then(module => {
          const adapter = new module.LocalOverrideAdapter(sdkKey);
          setOverrideAdapter(adapter);

          // Expose to window for console access in dev mode
          (window as any).statsigOverrides = adapter;

          // Add debug helper
          (window as any).statsigDebug = {
            showOverrides: () => adapter.getAllOverrides(),
            showStorage: () => {
              const keys = Object.keys(localStorage).filter(k => k.includes('local-overrides'));
              keys.forEach(k => console.log(k, localStorage.getItem(k)));
            },
          };
        })
        .catch(error => {
          console.warn('Failed to load @statsig/js-local-overrides:', error);
          // Package not installed - this is fine, just skip override functionality
        });
    }
  }, [sdkKey]);

  const options = {
    environment: {
      // Only alpha uses staging tier, everything else is production
      tier: import.meta.env.VITE_BUILD_ENV === 'alpha' ? 'staging' : 'production'
    },
    overrideAdapter,
    plugins: [
      new StatsigTriggeredSessionReplayPlugin({
        autoStartRecording: false, // Don't auto-start
        keepRollingWindow: true, // Keep buffer for on-demand capture
        rrwebConfig: {
          blockClass: 'rr-block', // CSS class to exclude elements
          maskTextClass: 'rr-mask', // CSS class to mask text
          sampling: {
            scroll: 50, // Sample scroll events
            input: 'last', // Only record last input
          },
        },
      }),
      autoCapturePlugin,
    ],
  };

  return (
    <BaseStatsigProvider
      sdkKey={sdkKey}
      user={{}} // Start with anonymous user, Statsig handles ID generation
      options={options}
    >
      <StatsigClientInitializer>{children}</StatsigClientInitializer>
    </BaseStatsigProvider>
  );
};
